// Thin fetch wrapper. Reads the base URL from VITE_WEB_API or defaults
// to same-origin (relative URLs). Same-origin is what we want when the
// production server serves the built renderer itself — the bundle then
// works whether visited at 127.0.0.1:8787, a Cloudflare tunnel, or any
// other host the server is reachable from. Set VITE_WEB_API only when
// the API lives on a different origin than the page (e.g. Vite dev on
// :5174 talking to the API on :8787).

import { getIdToken } from '../auth/firebase.js';
import { getActAs, getDeviceId, notifySessionLost } from '../auth/session.js';
import { WORK_BASE, IS_DESKTOP, LOCAL_API_KEY, baseFor, isAccountPath } from './runtime.js';

// On the web this is the only origin. On desktop it is the local server that
// does the work, while account calls are routed to audiostitcher.in instead —
// see api/runtime.js, which decides per path.
const API_BASE = WORK_BASE;

// Coarse gate in front of the API (X-API-Key on the server). This ships inside
// a public bundle, so it is not a secret and is not user authentication — that
// is the Firebase token below. It keeps unidentified traffic off the API.
// Leave VITE_API_KEY unset and the server's gate stays off.
export const API_KEY = import.meta.env.VITE_API_KEY || '';

// Merge the API key into a header bag.
//
// The two origins have different keys: the account server's ships in the
// bundle, while the local server mints a fresh one each launch and hands it to
// the page. Sending the wrong one is a 401, so the key follows the path.
export function withApiKey(headers = {}, path = '') {
  const key = IS_DESKTOP && !isAccountPath(path) ? LOCAL_API_KEY : API_KEY;
  return key ? { ...headers, 'X-API-Key': key } : { ...headers };
}

// Headers for every authenticated call: the API key, the Firebase ID token,
// the device id (single-session enforcement), and the superadmin's act-as
// target when one is selected (the server ignores X-Act-As for anyone else).
async function authHeaders(extra = {}, { forceRefresh = false, path = '' } = {}) {
  const headers = withApiKey(extra, path);
  const token = await getIdToken(forceRefresh);
  if (token) headers.Authorization = `Bearer ${token}`;
  headers['X-Device-Id'] = getDeviceId();
  const actAs = getActAs();
  if (actAs) headers['X-Act-As'] = actAs;
  return headers;
}

// The server's error code, read without consuming the response body.
async function errorCode(res) {
  try { return (await res.clone().json()).code || ''; } catch { return ''; }
}

// fetch() with auth headers. Every API call goes through here.
//
// A 401 for an expired or revoked token is retried once with a freshly minted
// one. The Firebase SDK refreshes tokens on its own schedule, so a call can
// land just after expiry, or just after the server started refusing a token;
// retrying turns that into an invisible hiccup instead of a failed action.
export async function authedFetch(path, init = {}) {
  const url = `${baseFor(path)}${path}`;
  const res = await fetch(url, { ...init, headers: await authHeaders(init.headers || {}, { path }) });
  if (res.status !== 401 || !(await getIdToken())) return res;
  const code = await errorCode(res);
  if (code !== 'token_invalid' && code !== 'auth_required') return res;
  const headers = await authHeaders(init.headers || {}, { forceRefresh: true, path });
  return fetch(url, { ...init, headers });
}

// For the few endpoints that need no signed-in user (registration). Still
// carries the API key, so the gate applies to them like everything else.
export function publicFetch(path, init = {}) {
  return fetch(`${baseFor(path)}${path}`, { ...init, headers: withApiKey(init.headers || {}, path) });
}

// ---------------------------------------------------------------------------
// Media tokens
// ---------------------------------------------------------------------------
// `<audio src>`, download links, `<img>` and the WebSocket are loaded by the
// browser itself, which will not attach an Authorization header, so their
// credential has to travel in the URL. The Firebase ID token used to go there.
// Now a short-lived media token does: the server only accepts it for read-only
// media GETs and the event stream, so a URL that leaks into a log or history
// cannot be used to change anything.
//
// The token is fetched once signed in (ensureMediaToken) and refreshed ahead of
// expiry, so URL builders can stay synchronous.
let mediaToken = '';
let mediaTokenExpiresAt = 0;
let mediaTokenTimer = null;
let mediaTokenInflight = null;
const MEDIA_REFRESH_MARGIN_MS = 10 * 60 * 1000;

export function getMediaToken() {
  return mediaToken;
}

export function ensureMediaToken() {
  if (mediaToken && Date.now() < mediaTokenExpiresAt - MEDIA_REFRESH_MARGIN_MS) {
    return Promise.resolve(mediaToken);
  }
  if (!mediaTokenInflight) {
    mediaTokenInflight = postJson('/api/auth/media-token', {})
      .then(({ token, expiresAt }) => {
        mediaToken = token;
        mediaTokenExpiresAt = expiresAt;
        scheduleMediaTokenRefresh();
        return token;
      })
      .finally(() => { mediaTokenInflight = null; });
  }
  return mediaTokenInflight;
}

function scheduleMediaTokenRefresh() {
  clearTimeout(mediaTokenTimer);
  const delay = Math.max(30_000, mediaTokenExpiresAt - Date.now() - MEDIA_REFRESH_MARGIN_MS);
  mediaTokenTimer = setTimeout(() => { ensureMediaToken().catch(() => {}); }, delay);
}

export function clearMediaToken() {
  clearTimeout(mediaTokenTimer);
  mediaToken = '';
  mediaTokenExpiresAt = 0;
  mediaTokenTimer = null;
}

// Background tabs throttle timers, so a laptop waking up can hold a stale
// token. Top it up whenever the page becomes visible again.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && mediaToken) ensureMediaToken().catch(() => {});
  });
}

// Query string for URLs the browser loads itself. Carries the media token,
// never the Firebase ID token.
export function authQuery() {
  const parts = [];
  if (mediaToken) parts.push(`media_token=${encodeURIComponent(mediaToken)}`);
  if (API_KEY) parts.push(`api_key=${encodeURIComponent(API_KEY)}`);
  parts.push(`device_id=${encodeURIComponent(getDeviceId())}`);
  const actAs = getActAs();
  if (actAs) parts.push(`act_as=${encodeURIComponent(actAs)}`);
  return parts.join('&');
}

const AAI_KEY_CODES = new Set(['assemblyai_key_required', 'assemblyai_key_invalid', 'assemblyai_no_credits']);

async function asJson(res) {
  const ct = res.headers.get('content-type') || '';
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    let code = '';
    if (ct.includes('application/json')) {
      try { const body = await res.json(); if (body && body.error) msg = body.error; if (body) code = body.code || ''; } catch {}
    } else {
      try { msg = await res.text(); } catch {}
    }
    // Single-session: this device lost the session to another device. Signal
    // the app to boot to the login screen with an explanation.
    if (res.status === 409 && code === 'session_superseded') notifySessionLost();
    // A missing, rejected or unfunded AssemblyAI key: let the app open the
    // key dialog wherever the user is.
    if (AAI_KEY_CODES.has(code)) {
      window.dispatchEvent(new CustomEvent('assemblyai-key-needed', { detail: { code, message: msg } }));
    }
    const err = new Error(msg);
    err.code = code;
    err.status = res.status;
    throw err;
  }
  if (ct.includes('application/json')) return res.json();
  return null;
}

export async function getJson(path) {
  return asJson(await authedFetch(path));
}

export async function postJson(path, body) {
  return asJson(await authedFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  }));
}

export async function putJson(path, body) {
  return asJson(await authedFetch(path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  }));
}

export async function patchJson(path, body) {
  return asJson(await authedFetch(path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  }));
}

export async function delJson(path) {
  return asJson(await authedFetch(path, { method: 'DELETE' }));
}

export async function uploadFiles(path, files, { fieldName = 'files', multi = true } = {}) {
  const fd = new FormData();
  if (multi) {
    for (const f of files) fd.append(fieldName, f);
  } else {
    fd.append(fieldName, files[0]);
  }
  return asJson(await authedFetch(path, { method: 'POST', body: fd }));
}

// XHR-based upload so we can observe progress events (the fetch API has
// no upload-progress hook). Resolves with the same parsed JSON shape as
// uploadFiles(). onProgress receives a 0..1 fraction. Caller is expected
// to handle per-file batching themselves — pass one File per call when
// per-file UI feedback is wanted.
export function uploadFilesWithProgress(path, files, {
  fieldName = 'files', multi = true, onProgress,
} = {}) {
  return new Promise(async (resolve, reject) => {
    const fd = new FormData();
    if (multi) {
      for (const f of files) fd.append(fieldName, f);
    } else {
      fd.append(fieldName, files[0]);
    }
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${baseFor(path)}${path}`);
    // Attach auth headers (token + act-as) before sending. XHR lets us set
    // request headers after open(), unlike a media element's src.
    try {
      const headers = await authHeaders({}, { path });
      for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    } catch {}
    if (typeof onProgress === 'function') {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) onProgress(e.loaded / e.total);
      });
    }
    xhr.onload = () => {
      const ct = xhr.getResponseHeader('content-type') || '';
      if (xhr.status >= 200 && xhr.status < 300) {
        if (ct.includes('application/json')) {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch (e) { reject(new Error('invalid JSON response')); }
        } else {
          resolve(null);
        }
        return;
      }
      let msg = `${xhr.status} ${xhr.statusText}`;
      if (ct.includes('application/json')) {
        try { const body = JSON.parse(xhr.responseText); if (body && body.error) msg = body.error; } catch {}
      } else if (xhr.responseText) {
        msg = xhr.responseText;
      }
      reject(new Error(msg));
    };
    xhr.onerror = () => reject(new Error('network error during upload'));
    xhr.onabort = () => reject(new Error('upload aborted'));
    xhr.send(fd);
  });
}

export function fileStreamUrl(id, { download = false } = {}) {
  if (!id) return '';
  const q = [download ? 'download=1' : '', authQuery()].filter(Boolean).join('&');
  return `${baseFor('/api/files')}/api/files/${encodeURIComponent(id)}${q ? `?${q}` : ''}`;
}

// URL that bundles several output ids into one .zip attachment. Used for
// stems so the browser saves a single file instead of N downloads.
export function filesZipUrl(ids, name) {
  const list = (ids || []).filter(Boolean);
  if (!list.length) return '';
  const q = new URLSearchParams();
  q.set('ids', list.join(','));
  if (name) q.set('name', name);
  const auth = authQuery();
  return `${baseFor('/api/files')}/api/files/zip?${q.toString()}${auth ? `&${auth}` : ''}`;
}

export { API_BASE };
